'use client'

import { useCallback, useEffect, useRef } from 'react'

import { API_URLS, getSelectedApiEnv } from '../env-config'
import type { NetworkRequest } from '../types'

const EXCLUDED_URL_PATTERNS = [
	'/_next/',
	'.hot-update.',
	'webpack',
	'__nextjs',
	'sockjs-node',
	'_next/static',
	'chrome-extension://',
	'mc.yandex.com',
	'mc.yandex.ru'
]

interface UseNetworkInterceptorOptions {
	onRequest: (request: Omit<NetworkRequest, 'id' | 'timestamp'>) => string
	onRequestUpdate: (id: string, updates: Partial<NetworkRequest>) => void
}

export const useNetworkInterceptor = ({ onRequest, onRequestUpdate }: UseNetworkInterceptorOptions): void => {
	const originalFetchRef = useRef<typeof fetch | null>(null)

	const shouldExcludeUrl = useCallback((url: string): boolean => {
		return EXCLUDED_URL_PATTERNS.some(pattern => url.includes(pattern))
	}, [])

	const interceptFetch = useCallback(
		(originalFetch: typeof fetch) => {
			return async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
				const startTime = Date.now()
				let url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url
				const method = init?.method || (input instanceof Request ? input.method : 'GET')

				if (shouldExcludeUrl(url)) {
					return originalFetch(input, init)
				}

				const selectedEnv = getSelectedApiEnv()
				let modifiedInput: RequestInfo | URL = input

				if (selectedEnv) {
					const targetHost = API_URLS[selectedEnv]
					const otherEnv = selectedEnv === 'prod' ? 'stage' : 'prod'
					const sourceHost = API_URLS[otherEnv]

					if (url.includes(sourceHost.replace('https://', ''))) {
						const rewrittenUrl = url.replace(sourceHost.replace('https://', ''), targetHost.replace('https://', ''))
						url = rewrittenUrl
						if (typeof input === 'string') {
							modifiedInput = rewrittenUrl
						} else if (input instanceof URL) {
							modifiedInput = new URL(rewrittenUrl)
						} else {
							modifiedInput = new Request(rewrittenUrl, input)
						}
					}
				}

				let requestBody: string | undefined
				const requestHeaders: Record<string, string> = {}

				// Capture request headers
				if (input instanceof Request) {
					input.headers.forEach((value, key) => {
						requestHeaders[key] = value
					})
				}
				if (init?.headers) {
					const headers = init.headers
					if (headers instanceof Headers) {
						headers.forEach((value, key) => {
							requestHeaders[key] = value
						})
					} else if (Array.isArray(headers)) {
						headers.forEach(([key, value]) => {
							requestHeaders[key] = value
						})
					} else {
						Object.entries(headers).forEach(([key, value]) => {
							requestHeaders[key] = value
						})
					}
				}

				// Try to get body from Request object first (before it's consumed)
				if (input instanceof Request && input.method !== 'GET' && input.method !== 'HEAD') {
					try {
						const clonedRequest = input.clone()
						requestBody = await clonedRequest.text()
					} catch {
						// body might already be consumed or not available
					}
				}

				// Then try init.body
				if (!requestBody) {
					const body = init?.body
					if (body) {
						try {
							if (typeof body === 'string') {
								requestBody = body
							} else if (body instanceof FormData) {
								const formDataObj: Record<string, string> = {}
								body.forEach((value, key) => {
									formDataObj[key] = value instanceof File ? `[File: ${value.name}]` : String(value)
								})
								requestBody = JSON.stringify(formDataObj, null, 2)
							} else if (body instanceof URLSearchParams) {
								requestBody = body.toString()
							} else if (body instanceof Blob) {
								requestBody = `[Blob: ${body.size} bytes, type: ${body.type}]`
							} else if (body instanceof ArrayBuffer) {
								requestBody = `[ArrayBuffer: ${body.byteLength} bytes]`
							} else if (typeof body === 'object') {
								requestBody = JSON.stringify(body, null, 2)
							} else {
								requestBody = String(body)
							}
						} catch {
							requestBody = '[Unable to serialize body]'
						}
					}
				}

				const currentRequestId = onRequest({
					url,
					method: method.toUpperCase(),
					pending: true,
					requestBody,
					requestHeaders: Object.keys(requestHeaders).length > 0 ? requestHeaders : undefined
				})

				try {
					const response = await originalFetch(modifiedInput, init)
					const duration = Date.now() - startTime

					let responseBody: string | undefined
					const responseHeaders: Record<string, string> = {}

					response.headers.forEach((value, key) => {
						responseHeaders[key] = value
					})

					const clonedResponse = response.clone()
					try {
						const text = await clonedResponse.text()
						responseBody = text.length > 5000 ? text.slice(0, 5000) + '...[truncated]' : text
					} catch {
						responseBody = '[Unable to read response]'
					}

					onRequestUpdate(currentRequestId, {
						status: response.status,
						duration,
						pending: false,
						responseBody,
						responseHeaders
					})

					return response
				} catch (error) {
					const duration = Date.now() - startTime

					onRequestUpdate(currentRequestId, {
						duration,
						pending: false,
						error: error instanceof Error ? error.message : 'Unknown error'
					})

					throw error
				}
			}
		},
		[onRequest, onRequestUpdate, shouldExcludeUrl]
	)

	useEffect(() => {
		if (typeof window === 'undefined') return

		originalFetchRef.current = window.fetch
		window.fetch = interceptFetch(originalFetchRef.current)

		return () => {
			if (originalFetchRef.current) {
				window.fetch = originalFetchRef.current
			}
		}
	}, [interceptFetch])
}
